// ================================================================
// Sitzungs-Wache — der sofortige Rauswurf, ohne auf die nächste Anfrage zu warten
// ================================================================
// Ohne diese Datei funktioniert alles trotzdem: wird die Sitzung entzogen,
// merkt die App es bei der nächsten Anfrage (apiClient fängt den 401 mit Grund
// ab). Nur dauert das bei einem offenen, aber gerade stillen Fenster beliebig
// lange — der Nutzer sähe eine Oberfläche, die er nicht mehr bedienen darf.
//
// Deshalb hängt sich die App zusätzlich an einen Ereignisstrom, der GENAU
// DIESER Sitzung gehört. Kommt dort «du wurdest abgemeldet» an, ist der
// Bildschirm in derselben Sekunde weg.
//
// Die Wache ist die KÜR, nicht die Pflicht: ein Handy in der Tasche hat keinen
// offenen Strom. Wer sie für die Durchsetzung der Regel hält, baut die Prüfung
// am Server ab — und genau dann wäre die Regel wieder nur eine Empfehlung.

import { rememberSignOut } from './signedOut.js';

let source = null;

/**
 * Startet die Wache. Mehrfach aufrufbar — ein zweiter Aufruf tut nichts.
 * @param {() => void} onRevoked  wird genau einmal gerufen, wenn der Rauswurf kommt
 */
export function startSessionWatch(onRevoked) {
  if (source) return;

  // EventSource kann keinen Authorization-Header senden. Statt dafür ein
  // zweites Token-Schema zu erfinden, läuft der Strom über den bestehenden
  // Weg: das Token im Query-String wäre hier falsch (es landete in jedem
  // Proxy-Log), deshalb nutzt die Wache `fetch` mit Header und liest den
  // Strom selbst — dieselbe Zeremonie, nur ohne EventSource.
  source = new AbortController();
  void readStream(source.signal, onRevoked);
}

export function stopSessionWatch() {
  if (source) { source.abort(); source = null; }
}

async function readStream(signal, onRevoked) {
  // Wiederverbinden mit wachsender Pause: ein Server-Neustart oder ein
  // Tunnel-Abbruch darf nicht in einen Hammer aus Verbindungsversuchen
  // umschlagen.
  let backoffMs = 2000;

  while (!signal.aborted) {
    try {
      const token = localStorage.getItem('levelupwerk:token');
      if (!token) return; // abgemeldet — nichts zu bewachen

      const res = await fetch('/api/auth/session-stream', {
        headers: { Authorization: 'Bearer ' + token },
        signal,
      });

      // 204 = Alt-Token ohne Sitzungsbezug. Es gibt nichts zu überwachen, und
      // ein Wiederverbinden würde daran nichts ändern.
      if (res.status === 204) return;
      // 401 heisst: die Sitzung ist bereits tot. Das Aufräumen übernimmt die
      // nächste gewöhnliche Anfrage über apiClient — hier nur aussteigen.
      if (res.status === 401) return;
      if (!res.ok || !res.body) throw new Error('stream failed');

      backoffMs = 2000; // Verbindung steht wieder
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE trennt Ereignisse durch eine Leerzeile.
        let split;
        while ((split = buffer.indexOf('\n\n')) !== -1) {
          const chunk = buffer.slice(0, split);
          buffer = buffer.slice(split + 2);
          const line = chunk.split('\n').find((l) => l.startsWith('data:'));
          if (!line) continue; // Heartbeat (': ping')
          let event;
          try { event = JSON.parse(line.slice(5).trim()); } catch { continue; }
          if (event?.type === 'session.revoked') {
            rememberSignOut(codeFor(event.reason), event.byDevice ?? null);
            stopSessionWatch();
            onRevoked();
            return;
          }
        }
      }
    } catch {
      if (signal.aborted) return;
    }

    if (signal.aborted) return;
    await new Promise((r) => setTimeout(r, backoffMs));
    backoffMs = Math.min(backoffMs * 2, 60_000);
  }
}

// Der Strom liefert den Grund als kurzen Schlüssel; die Oberfläche kennt die
// Fehlercodes. Hier eine Zeile Übersetzung, damit beide Wege — Live-Signal und
// 401 — am Ende denselben Text zeigen.
function codeFor(reason) {
  switch (reason) {
    case 'other_device': return 'AUTH_SESSION_REVOKED_OTHER_DEVICE';
    case 'password_changed': return 'AUTH_SESSION_REVOKED_PASSWORD_CHANGED';
    case 'manual': return 'AUTH_SESSION_REVOKED_MANUAL';
    default: return 'AUTH_SESSION_REVOKED';
  }
}
